import { type SupplierKind, type SupplierListItem, type SupplierListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { SupplierFormDrawer } from '../components/SupplierFormDrawer';
import { listSuppliers } from '../suppliers.api';
import {
  useBulkDeleteSuppliers,
  useBulkSetSuppliersStatus,
  useCurrencyOptions,
  useDeleteSupplier,
  useSuppliersList,
} from '../use-suppliers';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCanExportFinanceData, useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const KIND_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All kinds' },
  { value: 'DMC', label: 'DMC' },
  { value: 'HOTEL_AGGREGATOR', label: 'Hotel aggregator' },
  { value: 'AIRLINE_CONSOLIDATOR', label: 'Airline consolidator' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'VISA', label: 'Visa' },
  { value: 'OTHER', label: 'Other' },
];

const KIND_LABELS: Record<SupplierKind, string> = {
  DMC: 'DMC',
  HOTEL_AGGREGATOR: 'Hotel aggregator',
  AIRLINE_CONSOLIDATOR: 'Airline consolidator',
  TRANSPORT: 'Transport',
  ACTIVITY: 'Activity',
  INSURANCE: 'Insurance',
  VISA: 'Visa',
  OTHER: 'Other',
};

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'kind',
    label: 'Kind',
    options: KIND_FILTER_OPTIONS,
    widthClass: 'w-52',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const SuppliersListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canExportFinance = useCanExportFinanceData();
  const canDelete = me?.role === 'ADMIN';
  const deleteSupplier = useDeleteSupplier();
  const bulkDeleteMut = useBulkDeleteSuppliers();
  const bulkSetStatusMut = useBulkSetSuppliersStatus();
  const currenciesQuery = useCurrencyOptions();

  /** Map currency id → code for quick column resolution. */
  const currencyCodeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of currenciesQuery.data ?? []) {
      map.set(c.id, c.code);
    }
    return map;
  }, [currenciesQuery.data]);

  const columns: ListScreenColumns<SupplierListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<SupplierListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        cell: (row) => <span className="text-zinc-700">{KIND_LABELS[row.kind]}</span>,
      },
      {
        id: 'currency',
        header: 'Currency',
        cell: (row) => (
          <span className="text-zinc-600">
            {row.defaultCurrencyId ? (currencyCodeMap.get(row.defaultCurrencyId) ?? '-') : '-'}
          </span>
        ),
      },
      {
        id: 'contact',
        header: 'Contact',
        cell: (row) => (
          <span className="text-zinc-600">{row.contactName ?? row.contactEmail ?? '-'}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<SupplierListItem, SupplierListQuery>
      title="Suppliers"
      description="Vendors we purchase from - DMCs, hotel aggregators, consolidators, transport, activities, insurance, visa."
      useList={useSuppliersList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const kind = state.filters.kind as SupplierKind | '';
        return {
          ...base,
          ...(kind ? { kind } : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add supplier' }}
      renderDrawer={({ open, mode, row, close }) => (
        <SupplierFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          supplier={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} supplier${rows.length === 1 ? '' : 's'}?`,
            description: 'These suppliers will be soft-deleted and hidden from booking forms.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} supplier${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will appear in booking forms and pickers again.'
              : 'They will be hidden from pickers but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listSuppliers(q),
        filename: 'suppliers',
        canExport: canExportFinance,
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Kind', value: (r) => r.kind },
          { header: 'Contact name', value: (r) => r.contactName ?? '' },
          { header: 'Contact email', value: (r) => r.contactEmail ?? '' },
          { header: 'Contact phone', value: (r) => r.contactPhone ?? '' },
          {
            header: 'Currency',
            value: (r) =>
              r.defaultCurrencyId ? (currencyCodeMap.get(r.defaultCurrencyId) ?? '') : '',
          },
          { header: 'Credit days', value: (r) => r.creditDays ?? '' },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Legacy ID', value: (r) => r.legacyId ?? '' },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteSupplier.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this supplier?',
            description: `${row.name} will be soft-deleted and hidden from booking forms. Existing booking records referencing it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name, code or contact (min 3 characters)',
        emptyTitle: 'No suppliers match these filters',
        emptyDescription: 'Adjust the kind or status filter or clear the search box.',
        errorTitle: 'Failed to load suppliers',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
